import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchJobById } from '../../api/jobs';
import api from '../../api/http';

interface ProductProgress {
  productStatus: 'normal' | 'damaged' | 'not_found'; // 產品狀態
  productQuantity: number; // 產品數量（產品級別）
  services: {
    [serviceCode: string]: {
      completed: boolean;
      hasPhotos: boolean;
    };
  };
  allCompleted: boolean;
  damagePhotoCount?: number;
}

interface StoreProgress {
  products: {
    [productId: string]: ProductProgress;
  };
  additionalProducts?: any[]; // 臨時添加的產品
}

const StoreServicePage: React.FC = () => {
  const { jobId, storeId } = useParams<{ jobId: string; storeId: string }>();
  const navigate = useNavigate();
  
  const [progress, setProgress] = useState<StoreProgress>({ products: {} });
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [additionalProducts, setAdditionalProducts] = useState<any[]>([]); // 臨時添加的產品
  const [searchModel, setSearchModel] = useState(''); // 搜尋型號

  // 獲取 Job 詳情（包含 job_products 和 job_service_items）
  const {
    data: job,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => fetchJobById(jobId as string),
    enabled: Boolean(jobId),
  });

  // 從服務器加載草稿進度（跨設備/帳戶共享）
  useEffect(() => {
    const loadProgressFromServer = async () => {
      if (!jobId || !storeId) return;
      
      try {
        const response = await api.get('/service-drafts/by-store', {
          params: { job_id: jobId, store_id: storeId },
        });
        
        const drafts = response.data.drafts;
        
        if (drafts && Object.keys(drafts).length > 0) {
          // 從服務器草稿構建進度數據
          const serverProgress: StoreProgress = { products: {} };
          
          Object.entries(drafts).forEach(([productId, draft]: [string, any]) => {
            // 從 services_data 構建服務進度
            const services: { [key: string]: { completed: boolean; hasPhotos: boolean } } = {};
            
            // 先初始化 services_data 中的服務
            if (draft.services_data) {
              Object.entries(draft.services_data).forEach(([serviceCode, serviceData]: [string, any]) => {
                services[serviceCode] = {
                  completed: serviceData.completed || false,
                  hasPhotos: serviceData.hasPhotos || false,
                };
              });
            }
            
            // 根據照片數據來確定服務狀態
            if (draft.photos_data) {
              // 收集每個服務的前後照狀態
              const servicePhotos: { [serviceCode: string]: { hasBefore: boolean; hasAfter: boolean } } = {};
              
              Object.keys(draft.photos_data).forEach(key => {
                const photos = draft.photos_data[key];
                if (photos && photos.length > 0) {
                  // key 格式: "cleaning_before", "pop_replacement_after"
                  const lastUnderscoreIndex = key.lastIndexOf('_');
                  if (lastUnderscoreIndex > 0) {
                    const serviceCode = key.substring(0, lastUnderscoreIndex);
                    const photoType = key.substring(lastUnderscoreIndex + 1);
                    
                    if (!servicePhotos[serviceCode]) {
                      servicePhotos[serviceCode] = { hasBefore: false, hasAfter: false };
                    }
                    
                    if (photoType === 'before') {
                      servicePhotos[serviceCode].hasBefore = true;
                    } else if (photoType === 'after') {
                      servicePhotos[serviceCode].hasAfter = true;
                    }
                  }
                }
              });
              
              // 根據前後照狀態更新服務狀態
              Object.entries(servicePhotos).forEach(([serviceCode, photoStatus]) => {
                const hasPhotos = photoStatus.hasBefore && photoStatus.hasAfter;
                const completed = hasPhotos;
                
                if (!services[serviceCode]) {
                  services[serviceCode] = { completed: false, hasPhotos: false };
                }
                
                services[serviceCode].hasPhotos = hasPhotos;
                services[serviceCode].completed = completed;
              });
            }
            
            // 檢查是否所有服務都完成
            // 如果產品不存在，直接算作完成
            const productStatus = draft.product_status || 'normal';
            const allServicesCompleted = productStatus === 'not_found' 
              ? true 
              : Object.values(services).every(s => s.completed && s.hasPhotos);
            
            serverProgress.products[productId] = {
              productStatus,
              productQuantity: draft.product_quantity || 1,
              services,
              allCompleted: allServicesCompleted,
            };
          });
          
          setProgress(serverProgress);
          
          // 同步到 localStorage
          const key = `job_${jobId}_store_${storeId}_progress`;
          localStorage.setItem(key, JSON.stringify(serverProgress));
        } else {
          // 沒有服務器草稿，嘗試從 localStorage 加載
          const key = `job_${jobId}_store_${storeId}_progress`;
          const saved = localStorage.getItem(key);
          if (saved) {
            try {
              const data = JSON.parse(saved);
              setProgress(data);
              if (data.additionalProducts) {
                setAdditionalProducts(data.additionalProducts);
              }
            } catch (e) {
              console.error('Failed to parse progress:', e);
            }
          }
        }
      } catch (error) {
        console.error('從服務器加載進度失敗:', error);
        // 失敗時回退到 localStorage
        const key = `job_${jobId}_store_${storeId}_progress`;
        const saved = localStorage.getItem(key);
        if (saved) {
          try {
            const data = JSON.parse(saved);
            setProgress(data);
            if (data.additionalProducts) {
              setAdditionalProducts(data.additionalProducts);
            }
          } catch (e) {
            console.error('Failed to parse progress:', e);
          }
        }
      }
    };
    
    loadProgressFromServer();
  }, [jobId, storeId]);

  // 過濾出該店鋪的產品（包括原有的和臨時添加的）
  const storeProducts = React.useMemo(() => {
    if (!job?.job_products || !storeId) return [];
    const originalProducts = job.job_products.filter(jp => jp.store_id === Number(storeId));
    
    // 合併臨時添加的產品
    const additionalJobProducts = additionalProducts.map(product => ({
      id: -product.id, // 使用負數 ID 來標識臨時產品
      job_id: Number(jobId),
      store_id: Number(storeId),
      product_id: product.id,
      quantity_expected: 0,
      product: product,
    }));
    
    return [...originalProducts, ...additionalJobProducts];
  }, [job, storeId, additionalProducts, jobId]);

  // 根據搜尋詞過濾產品
  const filteredStoreProducts = React.useMemo(() => {
    if (!searchModel.trim()) return storeProducts;
    
    const term = searchModel.toLowerCase().trim();
    return storeProducts.filter(jp => {
      const product = jp.product;
      return (
        product?.model?.toLowerCase().includes(term) ||
        product?.name?.toLowerCase().includes(term) ||
        product?.product_name?.toLowerCase().includes(term)
      );
    });
  }, [storeProducts, searchModel]);

  // 檢查該店鋪是否已提交服務記錄
  const { data: submittedRecords } = useQuery({
    queryKey: ['service-records', 'by-job-store', jobId, storeId],
    queryFn: async () => {
      try {
        const { data } = await api.get('/service-records/by-job-store', {
          params: { job_id: jobId, store_id: storeId },
        });
        return data;
      } catch (error: any) {
        if (error.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: Boolean(jobId) && Boolean(storeId),
  });

  const isSubmitted = submittedRecords && submittedRecords.total_records > 0;

  // 計算當前店鋪的完成進度
  const completionStats = React.useMemo(() => {
    const total = storeProducts.length;
    
    // 如果已提交，所有產品都算完成
    if (isSubmitted) {
      return { total, completed: total };
    }
    
    // 否則從 localStorage 讀取進度
    const completed = storeProducts.filter(sp => 
      progress.products[sp.product_id]?.allCompleted
    ).length;
    return { total, completed };
  }, [storeProducts, progress, isSubmitted]);

  // 獲取當前店鋪資訊
  const currentStore = React.useMemo(() => {
    return job?.stores?.find(s => s.id === Number(storeId));
  }, [job, storeId]);

  const handleProductClick = (productId: number) => {
    // 無論是否提交，都進入編輯模式，允許修改照片
    navigate(`/jobs/${jobId}/store/${storeId}/product/${productId}`);
  };

  const handleSubmit = () => {
    // 檢查是否所有產品都已完成
    const allCompleted = storeProducts.every(sp => 
      progress.products[sp.product_id]?.allCompleted
    );

    if (!allCompleted) {
      alert('請完成所有產品的服務後再提交');
      return;
    }

    navigate(`/jobs/${jobId}/store/${storeId}/submit`);
  };


  if (isLoading) {
    return <div className="panel">載入中...</div>;
  }

  if (isError || !job) {
    return (
      <div className="panel">
        <p>無法載入工作資料</p>
        <Link to={`/jobs/${jobId}`}>返回工作詳情</Link>
      </div>
    );
  }

  return (
    <div className="panel">
      <Link to={`/jobs/${jobId}`} className="link-back">
        ← 返回工作詳情
      </Link>
      
      {/* 店鋪資訊 */}
      <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
        <h2 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.75rem' }}>
          {currentStore?.retailer?.name}
        </h2>
        
        {/* 詳細地址 */}
        <div style={{ fontSize: '1.125rem', color: '#6b7280', lineHeight: '1.6' }}>
          {/* 區域和地區 */}
          {(currentStore?.building?.district?.region?.name || currentStore?.building?.district?.name) && (
            <p style={{ margin: '0.25rem 0', fontWeight: 500 }}>
              {currentStore.building?.district?.region?.name || ''} {currentStore.building?.district?.name || ''}
            </p>
          )}
          
          {/* 建築物名稱 */}
          {currentStore?.building?.building_name && (
            <p style={{ margin: '0.25rem 0', fontWeight: 500 }}>
              {currentStore.building.building_name}
            </p>
          )}
          
          {/* 樓層和單位 */}
          {(currentStore?.floor || currentStore?.unit) && (
            <p style={{ margin: '0.25rem 0', fontWeight: 500 }}>
              {currentStore.floor && currentStore.floor}
              {currentStore.floor && currentStore.unit && ' '}
              {currentStore.unit && currentStore.unit}
            </p>
          )}
        </div>
      </div>

      {/* 進度顯示 */}
      <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '1.25rem', fontWeight: 600 }}>
            服務進度
          </span>
          <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#2563eb' }}>
            {completionStats.completed} / {completionStats.total}
          </span>
        </div>
        <div style={{ marginTop: '0.75rem', width: '100%', height: '12px', backgroundColor: '#dbeafe', borderRadius: '6px', overflow: 'hidden' }}>
          <div 
            style={{ 
              width: `${completionStats.total > 0 ? (completionStats.completed / completionStats.total) * 100 : 0}%`, 
              height: '100%', 
              backgroundColor: '#2563eb',
              transition: 'width 0.3s ease'
            }}
          />
        </div>
      </div>

      {/* 產品列表標題和新增按鈕 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>
          產品列表 {additionalProducts.length > 0 && (
            <span style={{ fontSize: '1.125rem', color: '#6b7280', fontWeight: 500 }}>
              (含 {additionalProducts.length} 個臨時添加的產品)
            </span>
          )}
        </h3>
        {!isSubmitted && (
          <button
            onClick={() => setShowAddProductModal(true)}
            className="btn-secondary"
            style={{
              fontSize: '1.125rem',
              fontWeight: 700,
              padding: '0.75rem 1.5rem',
              whiteSpace: 'nowrap',
            }}
          >
            + 新增產品
          </button>
        )}
      </div>

      {/* 搜尋型號 */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={searchModel}
            onChange={(e) => setSearchModel(e.target.value)}
            placeholder="🔍 搜尋型號或產品名稱..."
            style={{
              width: '100%',
              padding: '0.875rem 1rem',
              paddingRight: searchModel ? '3rem' : '1rem',
              fontSize: '1.125rem',
              fontWeight: 500,
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              outline: 'none',
              transition: 'border-color 0.2s',
            }}
            onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
            onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
          />
          {searchModel && (
            <button
              onClick={() => setSearchModel('')}
              style={{
                position: 'absolute',
                right: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                background: '#9ca3af',
                border: 'none',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '14px',
                fontWeight: 700,
              }}
            >
              ✕
            </button>
          )}
        </div>
        {searchModel && (
          <p style={{ marginTop: '0.5rem', fontSize: '1rem', color: '#6b7280' }}>
            找到 {filteredStoreProducts.length} 個產品
          </p>
        )}
      </div>
      
      {filteredStoreProducts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
          <p style={{ fontSize: '1.125rem' }}>
            {searchModel ? `找不到符合「${searchModel}」的產品` : '此店鋪沒有產品需要服務'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {filteredStoreProducts.map((jobProduct) => {
            const product = jobProduct.product;
            const productProgress = progress.products[product.id];
            const isCompleted = productProgress?.allCompleted;
            const productStatus = productProgress?.productStatus;
            const isDamaged = productStatus === 'damaged';
            const isNotFound = productStatus === 'not_found';
            
            return (
              <div
                key={jobProduct.id}
                onClick={() => handleProductClick(product.id)}
                style={{
                  padding: '1.5rem',
                  border: '2px solid',
                  borderColor: isCompleted ? '#10b981' : '#e5e7eb',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  backgroundColor: isCompleted ? '#f0fdf4' : 'white',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#2563eb';
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = isCompleted ? '#10b981' : '#e5e7eb';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      <h4 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>
                        {product.name}
                      </h4>
                      {isDamaged && (
                        <span style={{ 
                          fontSize: '1.125rem',
                          color: '#dc2626',
                          fontWeight: 700
                        }}>
                          ⚠️ 損壞
                        </span>
                      )}
                      {isNotFound && (
                        <span style={{ 
                          fontSize: '1.125rem',
                          color: '#6b7280',
                          fontWeight: 700
                        }}>
                          ❌ 不存在
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: '1.125rem', color: '#6b7280', marginBottom: '0.5rem', fontWeight: 500 }}>
                      型號：{product.model}
                    </p>
                    {product.item_category && (
                      <span style={{ 
                        display: 'inline-block',
                        padding: '0.25rem 0.75rem', 
                        backgroundColor: '#e0e7ff', 
                        color: '#4f46e5', 
                        borderRadius: '6px',
                        fontSize: '1rem',
                        fontWeight: 600
                      }}>
                        {product.item_category.name}
                      </span>
                    )}
                    
                    {/* 顯示照片完成狀態 */}
                    {productProgress && productStatus !== 'not_found' && (
                      <div style={{ marginTop: '0.75rem', fontSize: '1rem', color: '#6b7280' }}>
                        {Object.entries(productProgress.services).map(([serviceCode, serviceData]) => {
                          const serviceName = serviceCode === 'inventory' ? '盤點' : 
                                             serviceCode === 'pop_replacement' ? 'POP 更換' : 
                                             serviceCode === 'cleaning' ? '清潔' : serviceCode;
                          
                          return (
                            <div key={serviceCode} style={{ marginTop: '0.25rem', fontWeight: 500 }}>
                              {serviceData.completed ? (
                                <span style={{ color: '#10b981' }}>✓ {serviceName}：前後照已拍攝</span>
                              ) : (
                                <span style={{ color: '#f59e0b' }}>○ {serviceName}：需要拍攝前後照</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div style={{ marginLeft: '1rem' }}>
                    {isCompleted ? (
                      <span style={{ 
                        fontSize: '2rem',
                        color: '#10b981'
                      }}>
                        ✓
                      </span>
                    ) : (
                      <span style={{ 
                        fontSize: '1.5rem',
                        color: '#9ca3af'
                      }}>
                        ○
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 提交按鈕或已提交狀態 */}
      {storeProducts.length > 0 && (
        <div style={{ marginTop: '2rem' }}>
          {isSubmitted ? (
            <div
              style={{
                backgroundColor: '#d1fae5',
                border: '2px solid #10b981',
                borderRadius: '0.5rem',
                padding: '1.5rem',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: '1.5rem', color: '#10b981', marginBottom: '0.5rem' }}>
                ✓ 此店鋪已完成服務並提交
              </div>
              <div style={{ fontSize: '1rem', color: '#6b7280' }}>
                點擊產品卡片右上角的"查看記錄"按鈕可查看詳細服務記錄
              </div>
            </div>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={completionStats.completed !== completionStats.total}
              className="btn-primary"
              style={{
                width: '100%',
                opacity: completionStats.completed === completionStats.total ? 1 : 0.5,
                cursor: completionStats.completed === completionStats.total ? 'pointer' : 'not-allowed',
                fontSize: '1.25rem',
                fontWeight: 700,
                padding: '1rem',
              }}
            >
              {completionStats.completed === completionStats.total 
                ? '提交此店鋪的服務記錄' 
                : `請完成所有產品 (${completionStats.completed}/${completionStats.total})`
              }
            </button>
          )}
        </div>
      )}

      {/* 新增產品對話框 */}
      {showAddProductModal && (
        <AddProductModal 
          job={job} 
          storeProducts={storeProducts}
          onClose={() => setShowAddProductModal(false)}
          setAdditionalProducts={setAdditionalProducts}
          setProgress={setProgress}
        />
      )}
    </div>
  );
};

// 新增產品對話框組件
interface AddProductModalProps {
  job: any;
  storeProducts: any[];
  onClose: () => void;
  setAdditionalProducts: React.Dispatch<React.SetStateAction<any[]>>;
  setProgress: React.Dispatch<React.SetStateAction<StoreProgress>>;
}

const AddProductModal: React.FC<AddProductModalProps> = ({ job, storeProducts, onClose, setAdditionalProducts, setProgress }) => {
  const { jobId, storeId } = useParams<{ jobId: string; storeId: string }>();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);

  // 獲取所有產品列表
  const { data: allProducts, isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const { data } = await api.get('/products', { params: { status: 'active' } });
      return data;
    },
  });

  // 獲取已存在的產品 ID 列表
  const existingProductIds = React.useMemo(() => {
    return storeProducts.map((jp: any) => jp.product_id);
  }, [storeProducts]);

  // 過濾產品（排除已有的產品 + 搜索過濾）
  const filteredProducts = React.useMemo(() => {
    if (!allProducts) return [];
    
    // 先排除已經在列表中的產品
    let filtered = allProducts.filter((p: any) => !existingProductIds.includes(p.id));
    
    // 再根據搜索詞過濾
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((p: any) => 
        p.product_name.toLowerCase().includes(term) || 
        p.model?.toLowerCase().includes(term)
      );
    }
    
    return filtered;
  }, [allProducts, existingProductIds, searchTerm]);

  const handleAddProduct = () => {
    if (!selectedProduct || !jobId || !storeId) return;

    // 從 localStorage 加載現有進度
    const key = `job_${jobId}_store_${storeId}_progress`;
    const saved = localStorage.getItem(key);
    const data = saved ? JSON.parse(saved) : { products: {}, additionalProducts: [] };

    // 添加到臨時產品列表（不需要再檢查，因為列表已經過濾了）
    data.additionalProducts = data.additionalProducts || [];
    data.additionalProducts.push(selectedProduct);

    // 保存回 localStorage
    localStorage.setItem(key, JSON.stringify(data));

    // 更新 React 狀態（不使用 reload，避免路由問題）
    setAdditionalProducts(data.additionalProducts);
    setProgress(data);
    
    // 關閉對話框
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        padding: '1rem',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '2rem',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '80vh',
          overflow: 'auto',
        }}
      >
        <h2 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '1.5rem' }}>
          選擇產品
        </h2>

        {/* 搜索欄 */}
        <div style={{ marginBottom: '1.5rem' }}>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="搜索產品名稱或型號..."
            className="form-input"
            style={{
              width: '100%',
              fontSize: '1.125rem',
              padding: '0.75rem',
              fontWeight: 500,
            }}
          />
        </div>

        {/* 產品列表 */}
        {isLoading ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>載入中...</p>
        ) : (
          <div style={{ maxHeight: '400px', overflow: 'auto', marginBottom: '1.5rem' }}>
            {filteredProducts.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                沒有找到產品
              </p>
            ) : (
              filteredProducts.map((product: any) => (
                <div
                  key={product.id}
                  onClick={() => setSelectedProduct(product)}
                  style={{
                    padding: '1rem',
                    border: '2px solid',
                    borderColor: selectedProduct?.id === product.id ? '#2563eb' : '#e5e7eb',
                    borderRadius: '8px',
                    marginBottom: '0.75rem',
                    cursor: 'pointer',
                    backgroundColor: selectedProduct?.id === product.id ? '#eff6ff' : 'white',
                    transition: 'all 0.2s',
                  }}
                >
                  <h4 style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: '0.25rem' }}>
                    {product.product_name}
                  </h4>
                  <p style={{ fontSize: '1rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                    型號：{product.model}
                  </p>
                  {product.item_category && (
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.5rem',
                        backgroundColor: '#e0e7ff',
                        color: '#4f46e5',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                      }}
                    >
                      {product.item_category.name}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* 操作按鈕 */}
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button
            onClick={onClose}
            className="btn-secondary"
            style={{ flex: 1, fontSize: '1.125rem', fontWeight: 700, padding: '0.75rem' }}
          >
            取消
          </button>
          <button
            onClick={handleAddProduct}
            disabled={!selectedProduct}
            className="btn-primary"
            style={{
              flex: 1,
              fontSize: '1.125rem',
              fontWeight: 700,
              padding: '0.75rem',
              opacity: selectedProduct ? 1 : 0.5,
              cursor: selectedProduct ? 'pointer' : 'not-allowed',
            }}
          >
            確認添加
          </button>
        </div>
      </div>
    </div>
  );
};

export default StoreServicePage;

